import * as React from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

interface DialogContextValue {
  open: boolean
  onOpenChange: (open: boolean) => void
  titleId?: string
  descriptionId?: string
}

const DialogContext = React.createContext<DialogContextValue | undefined>(undefined)

export interface DialogProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  children: React.ReactNode
}

const Dialog = ({ open: controlledOpen, onOpenChange, children }: DialogProps) => {
  const [internalOpen, setInternalOpen] = React.useState(false)
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen
  const setOpen = onOpenChange || setInternalOpen
  const titleId = React.useId()
  const descriptionId = React.useId()

  return (
    <DialogContext.Provider value={{ open, onOpenChange: setOpen, titleId, descriptionId }}>
      {children}
    </DialogContext.Provider>
  )
}

const DialogTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & { asChild?: boolean }
>(({ className, onClick, ...props }, ref) => {
  const context = React.useContext(DialogContext)
  if (!context) throw new Error('DialogTrigger must be used within Dialog')

  return (
    <button
      ref={ref}
      type="button"
      className={className}
      onClick={(e) => {
        context.onOpenChange(true)
        onClick?.(e)
      }}
      {...props}
    />
  )
})
DialogTrigger.displayName = 'DialogTrigger'

const DialogContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    'aria-labelledby'?: string
    'aria-describedby'?: string | null
  }
>(({ className, children, 'aria-labelledby': ariaLabelledBy, 'aria-describedby': ariaDescribedBy, ...props }, ref) => {
  const context = React.useContext(DialogContext)
  if (!context) throw new Error('DialogContent must be used within Dialog')

  if (!context.open) return null

  const containsDialogTitle = (node: React.ReactNode): boolean => {
    let found = false
    React.Children.forEach(node, (child) => {
      if (found || !React.isValidElement(child)) return
      const displayName = (child.type as any)?.displayName
      if (displayName === 'DialogTitle') {
        found = true
        return
      }
      if (child.props?.children) {
        if (containsDialogTitle(child.props.children)) {
          found = true
        }
      }
    })
    return found
  }
  const containsDialogDescription = (node: React.ReactNode): boolean => {
    let found = false
    React.Children.forEach(node, (child) => {
      if (found || !React.isValidElement(child)) return
      const displayName = (child.type as any)?.displayName
      if (displayName === 'DialogDescription') {
        found = true
        return
      }
      if (child.props?.children) {
        if (containsDialogDescription(child.props.children)) {
          found = true
        }
      }
    })
    return found
  }
  const hasTitle = containsDialogTitle(children)
  const hasDescription = containsDialogDescription(children)

  // Use provided aria-describedby if explicitly set (including empty string), otherwise use context.descriptionId
  // This allows explicitly disabling description with aria-describedby=""
  const describedBy = ariaDescribedBy !== undefined ? ariaDescribedBy : context.descriptionId

  return (
    <>
      <div
        className="fixed inset-0 z-50 bg-black/50"
        onClick={() => context.onOpenChange(false)}
      />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabelledBy || context.titleId}
        aria-describedby={describedBy}
        className={cn(
          'fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border border-gray-200 bg-white p-6 shadow-lg duration-200 sm:rounded-lg',
          className
        )}
        {...props}
      >
        {!hasTitle && (
          <DialogTitle className="sr-only">Dialog</DialogTitle>
        )}
        {!hasDescription && ariaDescribedBy === undefined && (
          <DialogDescription className="sr-only">Dialog content</DialogDescription>
        )}
        {children}
        <button
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-gray-950 focus:ring-offset-2"
          onClick={() => context.onOpenChange(false)}
          aria-label="Close dialog"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>
      </div>
    </>
  )
})
DialogContent.displayName = 'DialogContent'

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-1.5 text-center sm:text-left',
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = 'DialogHeader'

const DialogTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, id, ...props }, ref) => {
  const context = React.useContext(DialogContext)
  if (!context) throw new Error('DialogTitle must be used within Dialog')
  
  return (
    <h2
      ref={ref}
      id={id || context.titleId}
      className={cn(
        'text-lg font-semibold leading-none tracking-tight',
        className
      )}
      {...props}
    />
  )
})
DialogTitle.displayName = 'DialogTitle'

const DialogDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, id, ...props }, ref) => {
  const context = React.useContext(DialogContext)
  if (!context) throw new Error('DialogDescription must be used within Dialog')
  
  return (
    <p
      ref={ref}
      id={id || context.descriptionId}
      className={cn('text-sm text-gray-500', className)}
      {...props}
    />
  )
})
DialogDescription.displayName = 'DialogDescription'

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = 'DialogFooter'

export {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
}



